// /foto/boka-fotograf-stockholm — körsida för Google Business Profile och
// annonser: en kort sida där kunden bokar ett fotoärende direkt, utan att
// först behöva läsa sig igenom hela ortssidan.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { FotoPriceList, FotoServiceSpec } from "@/components/site/foto/FotoCommerce";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { photosBy } from "@/content/photo-gallery";
import { PHOTOGRAPHER } from "@/content/foto";
import { aeoGeo, breadcrumbs, faqPage, photoBusiness } from "@/lib/seo/schema";

const PATH = "/foto/boka-fotograf-stockholm";
const TITLE = "Boka fotograf i Stockholm — svar inom en arbetsdag | Fenny";
const DESCRIPTION =
  "Boka fotograf i Stockholm för porträtt, kampanj, produktfoto, event eller drönare. Beskriv uppdraget i formuläret — Fenny svarar med fast pris inom en arbetsdag.";
const ANSWER = `Ni bokar fotograf i Stockholm genom att beskriva uppdrag, önskat datum och plats i formuläret på den här sidan. ${PHOTOGRAPHER.name} återkommer med upplägg och fast pris inom en arbetsdag, och vi har normalt lediga tider inom 5–10 arbetsdagar. Bokningen är inte bindande förrän ni godkänt offerten.`;

const FAQ = [
  {
    q: "Hur snabbt kan ni komma ut och fotografera i Stockholm?",
    a: "Normalt inom 5–10 arbetsdagar. Vid akuta behov löser vi ofta något inom 48 timmar — skriv det i meddelandet så prioriterar vi förfrågan.",
  },
  {
    q: "Är bokningen bindande?",
    a: "Nej. Ni får en offert med fast pris, och bokningen gäller först när ni godkänt den skriftligt. Avbokning senast fem arbetsdagar innan är kostnadsfri.",
  },
  {
    q: "Kan ni fotografera i våra lokaler?",
    a: "Ja. Vi bygger mobil studio på plats i hela Stockholmsområdet, inklusive Solna, Sundbyberg, Kista, Nacka och Täby. Vi behöver cirka 3 × 3 meter yta och ett eluttag.",
  },
  {
    q: "Vad kostar det?",
    a: "Företagsporträtt och headshots ligger i det lägre spannet, kampanj- och produktuppdrag högre. Prislistan längre ner är justerad för Stockholm och kommer ur vår studie Bildindex 2026.",
  },
];

export const Route = createFileRoute("/foto/boka-fotograf-stockholm")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Boka fotograf i Stockholm" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Hur bokar man fotograf i Stockholm?",
            answer: ANSWER,
            speakableSelectors: ["[data-quick-answer]"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          photoBusiness({
            city: "Stockholm",
            path: PATH,
            description: DESCRIPTION,
            images: photosBy("portratt").slice(0, 8),
            email: PHOTOGRAPHER.email,
            contactName: PHOTOGRAPHER.name,
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Boka fotograf i Stockholm", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: BokaFotografStockholm,
});

function BokaFotografStockholm() {
  return (
    <article className="pt-28">
      <Section>
        <div className="max-w-3xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Fotograf Stockholm
          </div>
          <h1 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
            Boka fotograf i Stockholm
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Beskriv uppdraget i formuläret nedan — uppdragstyp, önskat datum och plats.
            Fenny återkommer med upplägg och fast pris inom en arbetsdag. Inget är bindande
            förrän ni godkänt offerten.
          </p>
          <div className="mt-8">
            <QuickAnswer text={ANSWER} />
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#offert"
              className="inline-flex rounded-full bg-mint px-6 py-3 text-sm font-medium text-background transition hover:opacity-90"
            >
              Boka fotoärende
            </a>
            <a
              href={`mailto:${PHOTOGRAPHER.email}`}
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm transition hover:border-mint/50 hover:text-mint"
            >
              Mejla {PHOTOGRAPHER.name}
            </a>
          </div>
        </div>
      </Section>

      <FotoQuoteForm
        location="foto_boka_stockholm"
        defaultCity="Stockholm"
        title="Boka ert fotoärende i Stockholm"
        lede="Fyll i uppdrag, datum och plats. Ni får svar med upplägg och fast pris inom en arbetsdag — utan bindningstid."
      />

      <PhotoMasonry
        photos={photosBy("portratt").slice(0, 9)}
        eyebrow="Referens"
        heading="Bilder ur uppdrag i Stockholm"
        lede="Företagsporträtt fotograferade i studio och i kunders egna lokaler."
      />

      <FotoServiceSpec city="Stockholm" id="sa-gar-det-till-boka" />

      <FotoPriceList city="Stockholm" id="prislista-boka" />

      <Section>
        <EyebrowHeading eyebrow="Vanliga frågor" title="Frågor inför bokningen" />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-white/10 bg-surface/50 p-6">
              <h2 className="text-lg font-semibold">{f.q}</h2>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground">
          Vill ni läsa mer först?{" "}
          <Link
            to="/foto/stockholm"
            className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
          >
            Fotograf Stockholm
          </Link>{" "}
          ·{" "}
          <Link
            to="/foto/galleri"
            className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
          >
            Bildgalleriet
          </Link>
        </p>
      </Section>
    </article>
  );
}
